import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { Tag } from '../components/Tag';
import { PreVisualizacao, TemplateEditor } from '../components/TemplateEditor';
import type { BotaoTemplate } from '@mpa/shared/template-text';
import { Carregando, ErroBox, Modal, PageHeader, Vazio } from '../components/ui';

interface Blueprint {
  id: string;
  internalName: string;
  metaName: string | null;
  category: string;
  metaCategory: string;
  locale: string;
  purpose: string | null;
  _count: { clones: number };
  texto: {
    header: string | null;
    body: string;
    footer: string | null;
    botoes: BotaoTemplate[] | null;
    variaveis: { nome: string; exemplo: string | null }[];
  } | null;
}

const ROTULOS_CATEGORIA: Record<string, string> = {
  convite_jornada: 'Convite',
  lembrete: 'Lembrete',
  recomendacao: 'Recomendação',
  reabertura: 'Reabertura',
  pos_evento: 'Pós-evento',
};

/**
 * Catálogo de textos padrão da plataforma.
 *
 * O que mora aqui NÃO vai para a Meta. Template aprovado pertence a uma WABA, e
 * cada cliente tem a própria (decisão A1) — então cada organização clona o
 * blueprint para a conta dela e submete a cópia. É por isso que a coluna que
 * mais importa nesta tela é "clonado por": mexer num blueprint usado por doze
 * clientes não muda nada no que já foi aprovado, e é bom que isso fique visível.
 */
export function Blueprints() {
  const cliente = useQueryClient();
  const [editando, setEditando] = useState<Blueprint | 'novo' | null>(null);

  const consulta = useQuery({
    queryKey: ['master', 'blueprints'],
    queryFn: () => api<Blueprint[]>('/master/templates'),
  });

  const salvar = useMutation({
    mutationFn: ({ id, dados }: { id?: string; dados: Record<string, unknown> }) =>
      api(id ? `/master/templates/${id}` : '/master/templates', {
        method: id ? 'PATCH' : 'POST',
        body: dados,
      }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['master', 'blueprints'] });
      setEditando(null);
    },
  });

  const remover = useMutation({
    mutationFn: (id: string) => api(`/master/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['master', 'blueprints'] }),
  });

  return (
    <>
      <PageHeader
        titulo="Blueprints de template"
        subtitulo="Textos padrão da plataforma. Cada cliente clona para a WABA dele e submete a cópia à Meta."
        acoes={
          <button className="btn btn-primary" onClick={() => setEditando('novo')}>
            <i className="ti ti-plus" /> Novo blueprint
          </button>
        }
      />

      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}
      {remover.isError && <ErroBox erro={remover.error} />}

      {consulta.data &&
        (consulta.data.length === 0 ? (
          <Vazio
            icone="ti-template"
            titulo="Nenhum blueprint ainda"
            descricao="Sem texto padrão, cada cliente escreve o próprio do zero — e cada um erra por conta própria na análise da Meta."
            acao={
              <button className="btn btn-primary" onClick={() => setEditando('novo')}>
                <i className="ti ti-plus" /> Criar o primeiro
              </button>
            }
          />
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {consulta.data.map((bp) => (
              <div key={bp.id} className="card">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 16,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ minWidth: 260, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <strong style={{ fontSize: 14 }}>{bp.internalName}</strong>
                      <Tag type="muted">{ROTULOS_CATEGORIA[bp.category] ?? bp.category}</Tag>
                      <Tag type={bp.metaCategory === 'MARKETING' ? 'warning' : 'info'}>
                        {bp.metaCategory}
                      </Tag>
                      <Tag type="muted">{bp.locale}</Tag>
                    </div>

                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      {bp._count.clones === 0 ? (
                        'Nunca clonado.'
                      ) : (
                        <>
                          Clonado por <strong>{bp._count.clones}</strong> conta(s). Editar aqui{' '}
                          <strong>não</strong> altera o que já foi submetido.
                        </>
                      )}
                    </div>

                    {bp.purpose && (
                      <div style={{ fontSize: 13, marginTop: 8 }}>{bp.purpose}</div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditando(bp)}>
                      <i className="ti ti-pencil" /> Editar
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      disabled={bp._count.clones > 0}
                      title={
                        bp._count.clones > 0
                          ? 'Já foi clonado — apagá-lo apagaria a linhagem desses templates.'
                          : undefined
                      }
                      onClick={() => remover.mutate(bp.id)}
                    >
                      <i className="ti ti-trash" />
                    </button>
                  </div>
                </div>

                {bp.texto && (
                  <PreVisualizacao
                    header={bp.texto.header}
                    body={bp.texto.body}
                    footer={bp.texto.footer}
                    botoes={bp.texto.botoes}
                    recolhivel
                  />
                )}
              </div>
            ))}
          </div>
        ))}

      {editando && (
        <Modal
          titulo={editando === 'novo' ? 'Novo blueprint' : 'Editar blueprint'}
          descricao="Este texto não vai à Meta. Ele é o ponto de partida que cada cliente clona."
          aoFechar={() => setEditando(null)}
          largura={640}
        >
          <TemplateEditor
            inicial={
              editando === 'novo'
                ? undefined
                : {
                    internalName: editando.internalName,
                    category: editando.category,
                    locale: editando.locale,
                    purpose: editando.purpose ?? '',
                    header: editando.texto?.header ?? '',
                    body: editando.texto?.body ?? '',
                    footer: editando.texto?.footer ?? '',
                    botoes: editando.texto?.botoes ?? [],
                  }
            }
            aoSalvar={(dados) =>
              salvar.mutate({ id: editando === 'novo' ? undefined : editando.id, dados })
            }
            aoCancelar={() => setEditando(null)}
            salvando={salvar.isPending}
            erro={salvar.isError ? salvar.error : undefined}
            rotuloSalvar={editando === 'novo' ? 'Publicar no catálogo' : 'Salvar'}
          />
        </Modal>
      )}
    </>
  );
}
